import { useState } from 'react';
import { Delete } from 'lucide-react';
import { OptionChips } from '@verlo/shared/components/ui/segmented';

// What the guest handed over, and the change (Oct 2026 sweep). It was "Take £18.40 in cash,
// then confirm", with nothing kept of what was handed over, so the till couldn't say the
// change and a short drawer couldn't be traced. Quick notes for the usual case; a keypad,
// typed like a till (3, 0, 0, 0 is £30.00), for anything else.

/** The notes a guest is likely to hand over for `due`: exact, then the next round amounts. */
export function quickNotes(due) {
  const pence = Math.round(due * 100);
  // The next £5, the next two £10s, the next £20 and £50, above the bill.
  const next = (n, k = 1) => (Math.floor(pence / n) + k) * n;
  const up = new Set([next(500), next(1000), next(1000, 2), next(2000), next(5000)]);
  return [pence, ...[...up].sort((a, b) => a - b).slice(0, 3)].map((p) => p / 100);
}

/** Change for `tendered` against `due`, in pounds; negative when it's short. */
export const changeFor = (tendered, due) => (Math.round(tendered * 100) - Math.round(due * 100)) / 100;

/** More change than this is a slip of the keypad, not a guest's note (the server agrees). */
export const MAX_CHANGE = 100;

/** Can this be taken: 'ok', 'missing', 'short', or 'too_much' change. */
export function cashProblem(tendered, due) {
  if (tendered == null) return 'missing';
  const change = changeFor(tendered, due);
  if (change < 0) return 'short';
  if (change > MAX_CHANGE) return 'too_much';
  return 'ok';
}

/** A keypad press, in pence. Typing after a quick note starts a new amount: tapping £20 and
 *  then 2-0-0-0 made £20,002.00, which the server took. */
export function keypress(pence, key, fresh = false) {
  const from = fresh ? 0 : pence;
  if (key === 'back') return fresh ? 0 : Math.floor(from / 10);
  if (key === '00') return from * 100;
  return from * 10 + Number(key);
}

export default function CashTendered({ due, value, onChange, formatCurrency }) {
  const pence = value == null ? 0 : Math.round(value * 100);
  // The amount came from a quick note: the next key starts a new one.
  const [fresh, setFresh] = useState(false);
  const press = (k) => {
    const next = keypress(pence, k, fresh);
    setFresh(false);
    if (next > 10000000) return;   // £100,000: a fat finger, not a bill
    onChange(next ? next / 100 : null);
  };
  const pick = (v) => { setFresh(true); onChange(v); };
  const change = value == null ? null : changeFor(value, due);
  const notes = quickNotes(due);

  return (
    <div className="space-y-3 text-left" data-testid="cash-tendered">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-ink-soft">Handed over</span>
        <span className="font-heading text-3xl font-extrabold tabular-nums text-ink" data-testid="cash-tendered-amount">
          {value == null ? '£ —' : formatCurrency(value)}
        </span>
      </div>
      <OptionChips
        value={value}
        onChange={pick}
        options={notes.map((n, i) => ({ value: n, label: i === 0 ? 'Exact' : formatCurrency(n).replace('.00', ''), testId: i === 0 ? 'cash-exact' : `cash-note-${n}` }))}
        chipClassName="h-11 min-w-[4.5rem] text-[15px]"
      />
      <div className="grid grid-cols-3 gap-1.5">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '00', '0', 'back'].map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => press(k)}
            className="flex h-11 items-center justify-center border border-hairline bg-white font-heading text-lg font-bold text-ink hover:bg-surface-2 active:bg-paper"
            aria-label={k === 'back' ? 'Delete a digit' : k}
            data-testid={`cash-key-${k}`}
          >
            {k === 'back' ? <Delete size={18} /> : k}
          </button>
        ))}
      </div>
      {change != null && change > MAX_CHANGE && (
        <div className="border border-signal-stop-line bg-signal-stop-tint px-4 py-3 text-signal-stop" data-testid="cash-too-much">
          <span className="font-heading text-base font-bold">That's {formatCurrency(change)} change.</span> Check what was handed over.
        </div>
      )}
      {change != null && change >= 0 && change <= MAX_CHANGE && (
        <div className="flex items-center justify-between border border-signal-ok-line bg-signal-ok-tint px-4 py-3 text-signal-ok" data-testid="cash-change">
          <span className="font-heading text-base font-bold">{change > 0 ? 'Give change' : 'No change'}</span>
          <span className="font-heading text-3xl font-extrabold tabular-nums">{formatCurrency(change)}</span>
        </div>
      )}
      {change != null && change < 0 && (
        <div className="flex items-center justify-between border border-signal-warn-line bg-signal-warn-tint px-4 py-3 text-signal-warn" data-testid="cash-short">
          <span className="font-heading text-base font-bold">Short</span>
          <span className="font-heading text-2xl font-extrabold tabular-nums">{formatCurrency(-change)}</span>
        </div>
      )}
    </div>
  );
}
